import React, { useState } from "react";

type DatePickerProps = {
  onDateChange: (date: Date | null) => void; // Pass `null` when the date is invalid
};

const DatePicker: React.FC<DatePickerProps> = ({ onDateChange }) => {
  const [day, setDay] = useState<string>(""); // Start with empty string
  const [month, setMonth] = useState<string>(""); // Start with empty string
  const [year, setYear] = useState<string>(""); // Start with empty string
  const [isMonthEditing, setIsMonthEditing] = useState(false);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getMaxDaysInMonth = (month?: string, year?: number) => {
    if (!month || !year) return 31;
    const monthIndex = months.indexOf(month);
    const date = new Date(year, monthIndex + 1, 0);
    return date.getDate();
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setDay(value);

    if (value === "") {
      updateDate("", month, year);
      return;
    }

    const numValue = parseInt(value, 10);
    const maxDays = getMaxDaysInMonth(month, parseInt(year, 10));

    if (numValue >= 1 && numValue <= maxDays) {
      updateDate(value, month, year);
    } else if (numValue > maxDays) {
      setDay(maxDays.toString());
      updateDate(maxDays.toString(), month, year);
    } else if (numValue <= 0) {
      setDay("1");
      updateDate("1", month, year);
    }
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setMonth(value);

    if (value === "") {
      updateDate(day, "", year);
      return;
    }

    if (months.includes(value)) {
      updateDate(day, value, year);
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setYear(value);

    if (value === "") {
      updateDate(day, month, "");
      return;
    }

    const numValue = parseInt(value, 10);
    if (numValue >= 1900 && numValue <= 2200) {
      updateDate(day, month, value);
    }
  };

  const updateDate = (day: string, month: string, year: string) => {
    if (!day || !month || !year) {
      onDateChange(null); // Notify parent that the date is invalid or incomplete
      return;
    }

    const monthIndex = months.indexOf(month);
    if (monthIndex === -1) return;

    const date = new Date(parseInt(year, 10), monthIndex, parseInt(day, 10));
    if (!isNaN(date.getTime())) {
      onDateChange(date);
    } else {
      onDateChange(null);
    }
  };

  const handleMonthBlur = () => {
    if (isMonthEditing) {
      const numValue = parseInt(month, 10);
      if (numValue >= 1 && numValue <= 12) {
        setMonth(months[numValue - 1]);
      }
    }
    setIsMonthEditing(false);
    updateDate(day, month, year);
  };

  const handleMonthFocus = () => {
    const numValue = months.indexOf(month) + 1;
    if (numValue > 0) {
      setMonth(numValue.toString());
    }
    setIsMonthEditing(true);
  };

  const styles = {
    container: {
      display: "flex",
      flexDirection: "row" as "row",
      fontFamily: "Arial, sans-serif",
      color: "#32AAFF",
      alignItems: "center",
    },
    datePickerContainer: {
      display: "flex",
      alignItems: "center",
    },
    input: {
      border: "1px solid #32CCFF",
      borderRadius: "20px",
      textAlign: "center" as "center",
      fontSize: "16px",
      backgroundColor: "#E6F9FF",
      color: "#32AAFF",
      outline: "none",
      transition: "all 0.3s ease",
      height: 32,
    },
    inputFocus: {
      borderColor: "#0056b3",
      boxShadow: "0 0 5px rgba(0, 127, 255, 0.5)",
    },
    monthInput: {
      width: "100px",
    },
    inputSmall: {
      width: "60px",
    },
  };

  return (
    <div style={styles.datePickerContainer}>
      <input
        type="text"
        value={day}
        onChange={handleDayChange}
        maxLength={2}
        style={{
          ...styles.input,
          ...styles.inputSmall,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }}
        placeholder="DD"
      />
      <input
        type="text"
        value={month}
        onChange={handleMonthChange}
        onBlur={handleMonthBlur}
        onFocus={handleMonthFocus}
        style={{ ...styles.input, ...styles.monthInput, borderRadius: 0 }}
        placeholder="Month"
      />
      <input
        type="text"
        value={year}
        onChange={handleYearChange}
        maxLength={4}
        style={{
          ...styles.input,
          ...styles.inputSmall,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        }}
        placeholder="YYYY"
      />
    </div>
  );
};

export default DatePicker;
